import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Upload, Layers, FileText } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { InboxInvoiceDrawer } from '../../components/InboxInvoiceDrawer';
import { ApInvoiceCreateDrawer } from '../../components/ApInvoiceCreateDrawer';
import { useApInvoices, useUploadApInvoice, useBulkUploadAp, ApInvoice } from '../../hooks/useApInvoices';
import { QueryError } from '../../components/ui/QueryError';
import { EmptyState } from '../../components/ui/EmptyState';
import { ListTruncationNote } from '../../components/ui/ListTruncationNote';
import { formatAmount, formatDate } from '../../lib/format';
import { useCan } from '../../hooks/useCan';

const STATUSES: Array<ApInvoice['status']> = [
  'pending_review', 'approved', 'scheduled', 'paid', 'disputed', 'void',
];

const TONE: Record<ApInvoice['status'], 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  pending_review: 'accent',
  approved: 'success',
  scheduled: 'success',
  paid: 'muted',
  disputed: 'warn',
  void: 'muted',
};

const MATCH_TONE: Record<ApInvoice['match_status'], 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  no_po: 'muted',
  matched: 'success',
  partial: 'accent',
  mismatch: 'warn',
};

const DOC_TYPES = ['invoice', 'receipt', 'quote', 'purchase_order', 'delivery_note', 'inspection', 'contract', 'other'] as const;
const MATCH_STATUSES = ['no_po', 'matched', 'partial', 'mismatch'] as const;

export function InvoicesApPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const [status, setStatus] = useState<string>('');
  const [docType, setDocType] = useState<string>('');
  const [matchStatus, setMatchStatus] = useState<string>('');
  const [reviewOnly, setReviewOnly] = useState(false);
  const { data: invoices, isLoading, isError, refetch } = useApInvoices(wsId, {
    ...(status ? { status } : {}),
    ...(docType ? { docType } : {}),
    ...(matchStatus ? { matchStatus } : {}),
    ...(reviewOnly ? { reviewOnly: '1' } : {}),
  });
  const upload = useUploadApInvoice(wsId);
  const bulkUpload = useBulkUploadAp(wsId);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);

  // Deep-link support: another surface (an approval row, a signal) can link to
  // `…/ap-invoices?open=<id>` to open the invoice drawer directly. We mirror the
  // open drawer back into the URL so the link is shareable and the back button
  // closes the drawer rather than leaving the page.
  useEffect(() => {
    const open = searchParams.get('open');
    if (open && open !== drawerId) setDrawerId(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const openDrawer = (id: string | null) => {
    setDrawerId(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('open', id);
        else next.delete('open');
        return next;
      },
      { replace: true },
    );
  };
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [splitPdf, setSplitPdf] = useState(true);
  const [bulkResult, setBulkResult] = useState<{ count: number; failed: number } | null>(null);

  const handleFile = async (file: File) => {
    setUploadErr(null);
    try {
      const created = await upload.mutateAsync(file);
      // Open the freshly-uploaded invoice's drawer so the user can
      // verify the extracted fields immediately.
      if (created?.id) openDrawer(created.id);
    } catch (err: any) {
      setUploadErr(err?.response?.data?.message ?? err?.message ?? 'Upload failed');
    }
  };

  const handleBulkFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setUploadErr(null);
    setBulkResult(null);
    try {
      const res = await bulkUpload.mutateAsync({ files, splitPdfPages: splitPdf });
      setBulkResult({ count: res.count, failed: res.failed?.length ?? 0 });
    } catch (err: any) {
      setUploadErr(err?.response?.data?.message ?? err?.message ?? 'Bulk upload failed');
    }
  };

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h1 className="display text-2xl font-semibold">{t('invoicesAp.title')}</h1>
          <div className="flex items-center gap-3">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm"
            >
              <option value="">{t('common.allStatus')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{t(`invoicesAp.statuses.${s}`)}</option>
              ))}
            </select>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm"
            >
              <option value="">{t('invoicesAp.allDocTypes')}</option>
              {DOC_TYPES.map((d) => (
                <option key={d} value={d}>{t(`invoicesAp.docTypes.${d}`)}</option>
              ))}
            </select>
            <select
              value={matchStatus}
              onChange={(e) => setMatchStatus(e.target.value)}
              className="h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm"
            >
              <option value="">{t('invoicesAp.allMatch')}</option>
              {MATCH_STATUSES.map((m) => (
                <option key={m} value={m}>{t(`invoicesAp.matchStatuses.${m}`)}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setReviewOnly((v) => !v)}
              aria-pressed={reviewOnly}
              className={
                'inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ' +
                (reviewOnly
                  ? 'border-warn-400 bg-warn-50 dark:bg-warn-900/20 text-warn-700 dark:text-warn-300'
                  : 'border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900')
              }
            >
              {t('invoicesAp.reviewFilter')}
            </button>
            {canMutate && (
              <>
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 text-sm font-medium"
                >
                  <Plus size={14} />
                  {t('invoicesAp.createInvoice')}
                </button>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 cursor-pointer text-sm font-medium">
                  <Upload size={14} />
                  {t('inbox.uploadInvoice')}
                  <input
                    type="file"
                    hidden
                    accept="application/pdf,image/*,text/html,text/plain,.pdf,.html,.htm,.txt"
                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                  />
                </label>
                <label
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 cursor-pointer text-sm font-medium"
                  title={t('invoicesAp.bulkUploadHint')}
                >
                  <Layers size={14} />
                  {bulkUpload.isPending ? t('common.loading') : t('invoicesAp.bulkUpload')}
                  <input
                    type="file"
                    hidden
                    multiple
                    disabled={bulkUpload.isPending}
                    accept="application/pdf,image/*,.pdf,.xlsx,.csv,.xml,.html,.htm,.txt"
                    onChange={(e) => {
                      if (e.target.files) handleBulkFiles(Array.from(e.target.files));
                      e.target.value = '';
                    }}
                  />
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-ink-600 dark:text-ink-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={splitPdf}
                    onChange={(e) => setSplitPdf(e.target.checked)}
                  />
                  {t('invoicesAp.splitPdfPages')}
                </label>
              </>
            )}
          </div>
        </div>

        {uploadErr && <div className="mb-4 text-sm text-warn-600">{uploadErr}</div>}
        {bulkResult !== null && (
          <div className="mb-4 text-sm">
            <span className="text-success-600">{t('invoicesAp.bulkResult', { count: bulkResult.count })}</span>
            {bulkResult.failed > 0 && (
              <span className="ml-2 text-warn-600">{t('invoicesAp.bulkFailed', { count: bulkResult.failed })}</span>
            )}
          </div>
        )}

        <Card>
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : isError ? (
            <QueryError onRetry={refetch} />
          ) : !invoices || invoices.length === 0 ? (
            <EmptyState
              icon={FileText}
              title={t('emptyState.invoicesAp.title')}
              description={t('emptyState.invoicesAp.body')}
              primaryLabel={t('invoicesAp.createInvoice')}
              primaryIcon={Plus}
              onPrimary={() => setCreateOpen(true)}
              offerSampleData={!status && !docType && !matchStatus && !reviewOnly}
              wsId={wsId}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-ink-500 border-b border-ink-200 dark:border-ink-800">
                    <th className="px-5 py-3 font-medium">{t('invoicesAp.invoiceNumber')}</th>
                    <th className="px-5 py-3 font-medium">{t('invoicesAp.docType')}</th>
                    <th className="px-5 py-3 font-medium">{t('common.issue')}</th>
                    <th className="px-5 py-3 font-medium">{t('common.due')}</th>
                    <th className="px-5 py-3 font-medium">{t('common.status')}</th>
                    <th className="px-5 py-3 font-medium">{t('invoicesAp.matchStatus')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('common.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr
                      key={inv.id}
                      onClick={() => openDrawer(inv.id)}
                      className="border-b border-ink-200 dark:border-ink-800 hover:bg-ink-50 dark:hover:bg-ink-900 cursor-pointer"
                    >
                      <td className="px-5 py-3">{inv.invoice_number ?? '—'}</td>
                      <td className="px-5 py-3">
                        <Pill tone={inv.doc_type === 'invoice' ? 'neutral' : 'muted'}>
                          {t(`invoicesAp.docTypes.${inv.doc_type}`)}
                        </Pill>
                      </td>
                      <td className="px-5 py-3">{formatDate(inv.issue_date, i18n.language)}</td>
                      <td className="px-5 py-3">{formatDate(inv.due_date, i18n.language)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Pill tone={TONE[inv.status]}>{t(`invoicesAp.statuses.${inv.status}`)}</Pill>
                          {inv.posting_review_reason && (
                            <Pill tone="warn">{t('invoicesAp.needsReview')}</Pill>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <Pill tone={MATCH_TONE[inv.match_status]}>{t(`invoicesAp.matchStatuses.${inv.match_status}`)}</Pill>
                      </td>
                      <td className="px-5 py-3 text-right num">{formatAmount(inv.total_cents, inv.currency, i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ListTruncationNote count={invoices.length} cap={100} />
            </div>
          )}
        </Card>
      </div>

      {wsId && (
        <>
          <InboxInvoiceDrawer
            wsId={wsId}
            invoiceId={drawerId}
            open={!!drawerId}
            onOpenChange={(o) => !o && openDrawer(null)}
            editable={canMutate}
          />
          <ApInvoiceCreateDrawer
            wsId={wsId}
            open={createOpen}
            onOpenChange={setCreateOpen}
            onCreated={(id) => openDrawer(id)}
          />
        </>
      )}
    </AppShell>
  );
}
